import React from "react";
import {Table} from "semantic-ui-react";
import {TicketDetails} from "./TicketModels";
import dataFormatUtil from "@/util/dataFormatUtil";

interface TicketTableProps {
    ticket: TicketDetails;
}

interface TicketTotalRowProps {
    label: string;
    value: number;
    reasonDescription?: string;
}

interface TicketItemsDetailsProps {
    ticket: TicketDetails;
}

const getValueFormattingClass = (value: number) => {
    return value < 0 ? "ticket-negative-value" : "";
};

const TicketTotalRow: React.FC<TicketTotalRowProps> = ({
    label,
    value,
    reasonDescription
}) => {
    return (
        <Table.Row className="ticket-total-row">
            <Table.Cell></Table.Cell>
            <Table.Cell></Table.Cell>
            <Table.Cell textAlign="right">
                <b>{label}</b>
            </Table.Cell>
            <Table.Cell className={getValueFormattingClass(value)} textAlign="right">
                {dataFormatUtil.formatMoney(Math.abs(value), 2)}
            </Table.Cell>
            <Table.Cell>{reasonDescription || ""}</Table.Cell>
        </Table.Row>
    );
};

const TicketTableBody: React.FC<TicketItemsDetailsProps> = ({ticket}) => {
    // The API returns items in order, with each parent line (null isPartOf)
    // immediately followed by its modifier/sub-items (non-null isPartOf).
    // isPartOf is a group identifier from the API, not the parent's array index,
    // so we preserve the existing order rather than trying to re-key by isPartOf.
    const sortedItems = ticket.transactionItems;

    return (
        <Table.Body>
            {sortedItems.map((item, index) => {
                const spacingClass = item.isPartOf ? "sub-item-non-delete" : "";
                const descriptionModifier = item.reason ? item.reason : "";
                const quantity = item.quantity || 0;
                const price = item.price || 0;
                item.number;
                return (
                    <Table.Row className="table-item" key={index}>
                        <Table.Cell
                            className={getValueFormattingClass(quantity)}
                            textAlign="center"
                        >
                            {quantity < 0 ? `(${Math.abs(quantity)})` : quantity}
                        </Table.Cell>
                        <Table.Cell>{item.number}</Table.Cell>
                        <Table.Cell className={spacingClass}>
                            {item.isPartOf ? (
                                <span className="sub-item-content">
                                    {descriptionModifier
                                        ? `${descriptionModifier} - `
                                        : ""}
                                    {item.description}
                                </span>
                            ) : (
                                <>
                                    {descriptionModifier
                                        ? `${descriptionModifier} - `
                                        : ""}
                                    {item.description}
                                </>
                            )}
                        </Table.Cell>
                        <Table.Cell
                            className={getValueFormattingClass(quantity)}
                            textAlign="right"
                        >
                            {item.price !== null && item.price !== undefined
                                ? quantity < 0
                                    ? `(${dataFormatUtil.formatMoney(
                                          Math.abs(price),
                                          2
                                      )})`
                                    : dataFormatUtil.formatMoney(price, 2)
                                : ""}
                        </Table.Cell>
                        <Table.Cell>{item.reason}</Table.Cell>
                    </Table.Row>
                );
            })}

            <Table.Row>
                <Table.Cell colSpan={5}> </Table.Cell>
            </Table.Row>

            {ticket.discountAmount !== 0 && (
                <TicketTotalRow
                    label="Discount:"
                    value={ticket.discountAmount}
                    reasonDescription={ticket.discountReason}
                />
            )}

            <TicketTotalRow label="Subtotal:" value={ticket.subTotal} />
            <TicketTotalRow label="Sales Tax:" value={ticket.salesTax} />
            <TicketTotalRow
                label={`${ticket.channelDescription} Total:`}
                value={ticket.total}
            />

            {ticket.paymentMethods.map((payment, index) => {
                return (
                    <React.Fragment key={index}>
                        {payment.tipAmount !== 0 && (
                            <TicketTotalRow
                                label={`Tips (${payment.paymentDescription}):`}
                                value={payment.tipAmount}
                            />
                        )}

                        {payment.amount !== 0 && (
                            <TicketTotalRow
                                label={`${payment.paymentDescription}:`}
                                value={payment.amount}
                                reasonDescription={payment.reasonDescription}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {ticket.changeAmount !== 0 && (
                <TicketTotalRow label="Change:" value={ticket.changeAmount} />
            )}
        </Table.Body>
    );
};

export const TicketTable: React.FC<TicketTableProps> = ({ticket}) => {
    return (
        <Table basic="very">
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Qty</Table.HeaderCell>
                    <Table.HeaderCell>Number</Table.HeaderCell>
                    <Table.HeaderCell>Description</Table.HeaderCell>
                    <Table.HeaderCell textAlign="right">Price</Table.HeaderCell>
                    <Table.HeaderCell>Reason</Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <TicketTableBody ticket={ticket} />
        </Table>
    );
};
